import { logger } from '@adornis/base/logging.js';
import { SetBehaviorSubject } from '@adornis/base/rxjs-extension/index.js';
import type { Maybe, ValueEvent } from '@adornis/base/utilTypes.js';
import { selectionSet, type NarrowedEntity } from '@adornis/baseql/utils/queryGeneration.js';
import { customMailRequestDialog } from '@adornis/bh-mails/custom-mail-request.js';
import { getBuddyMailTemplates } from '@adornis/bh-matching-mails/api/mail-templates.js';
import { getUserMailVars } from '@adornis/bh-matching-mails/api/user-mail.js';
import { BuddyMailTemplate } from '@adornis/bh-matching-mails/entities/mail-templates.js';
import {
  AutoMatchUserEntry,
  autoMatchUsers,
  automatichingRuleDescription,
} from '@adornis/bh-matching-matches/api/auto-matching.js';
import { MatchAPI } from '@adornis/bh-matching-matches/api/matchAPI.js';
import { Match } from '@adornis/bh-matching-matches/entities/match.js';
import { HiddenSortingMachingUserFields } from '@adornis/bh-matching-matches/static/HiddenSortingMachingUserFields.js';
import { currentlySelectedTimeframe } from '@adornis/bh-matching-timeframes/components/matching-semester-selection.js';
import type { TranslationDictionaryBuddy } from '@adornis/bh-matching-translation/translations.js';
import { MatchingUserAPI, sendCustomMatchingMail } from '@adornis/bh-matching-user/api/MatchingUserAPI.js';
import {
  BuddyUser,
  BuddyUserMentee,
  BuddyUserMentor,
  DefaultMenteeUser,
  DefaultMentorUser,
} from '@adornis/bh-matching-user/entities/buddy-user.js';
import { getMenteeUserClass, getMentorUserClass } from '@adornis/bh-matching-user/user-class.js';
import { BHFilterEditor } from '@adornis/buchungsheld-components/bh-filter-editor.js';
import type { User } from '@adornis/buchungsheld-db/entities/user.js';
import '@adornis/buchungsheld-theme/components/tr-toggle-content.js';
import '@adornis/buchungsheld-theme/components/tr-vr.js';
import '@adornis/buchungsheld-theme/fonts/fonts.js';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { WindowSizeController } from '@adornis/chemistry/controllers/window-size-controller.js';
import { acss } from '@adornis/chemistry/directives/acss.js';
import { css } from '@adornis/chemistry/directives/css.js';
import { virtualize } from '@adornis/chemistry/directives/when-seen.js';
import '@adornis/chemistry/elements/components/x-button.js';
import '@adornis/chemistry/elements/components/x-card.js';
import '@adornis/chemistry/elements/components/x-flex.js';
import '@adornis/chemistry/elements/components/x-grid.js';
import '@adornis/chemistry/elements/components/x-hr.js';
import '@adornis/chemistry/elements/components/x-icon.js';
import '@adornis/chemistry/elements/components/x-loading-state.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import '@adornis/chemistry/elements/components/x-toggle-content.js';
import { buildFullOptions } from '@adornis/data-view/client/data-view-options.js';
import { View } from '@adornis/data-view/db/view.js';
import { XDialog } from '@adornis/dialog/x-dialog.js';
import { XNativeDialog } from '@adornis/dialog/x-native-dialog.js';
import '@adornis/dragdrop/x-drag-drop.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { IntVariations } from '@adornis/filter/aspect-schemas/IntAspect.js';
import { mailTemplateDefinitions } from '@adornis/mails/db/mail-template-definition.js';
import { SubscriptionPagination } from '@adornis/pagination/paginatable.js';
import '@adornis/popover/x-dropdown-selection.js';
import '@adornis/popover/x-tooltip.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { TranslationController } from '@adornis/translation-core/client/translation-controller.js';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';
import { combineLatest, firstValueFrom, Observable, of, switchMap } from 'rxjs';
import './buddy-user-details.js';

const searchFields = ['firstName', 'lastName', 'email'];

/**
 * @element buddy-page-matching
 */
@customElement('buddy-page-matching')
export class BuddyPageMatching extends ChemistryLitElement {
  @property({ attribute: false }) menteeUserClass: typeof BuddyUserMentee = DefaultMenteeUser;
  @property({ attribute: false }) mentorUserClass: typeof BuddyUserMentor = DefaultMentorUser;
  @state() private _mentorsOpen = true;
  @state() private _menteeOpen = true;

  protected _translator = new TranslationController<TranslationDictionaryBuddy>(this);
  @state() protected _currentlySelectedTimeframe = new RXController(this, currentlySelectedTimeframe);

  protected _personTogleComponentName = 'buddy-user-details';

  @state() private _showMentorSorting = false;
  private _mentorFilter = new AdornisFilter({ searchFields });

  protected mentorPagination = new SubscriptionPagination(
    (skip: number, limit: number, filter: AdornisFilter) => () =>
      this._currentlySelectedTimeframe.observable.pipe(
        switchMap(timeframe =>
          timeframe
            ? subscribeUser(BuddyUserMentor, this.mentorUserClass._class, timeframe._id, filter, skip, limit)
            : of([]),
        ),
      ),
    [this._mentorFilter],
    selectionSet(() => this.mentorUserClass, 3),
  );

  protected totalMentors = new RXController(
    this,
    combineLatest([this._currentlySelectedTimeframe.observable, this._mentorFilter.whenChanging]).pipe(
      switchMap(async ([timeframe, filter]) => {
        if (!timeframe) return 0;
        const result = await MatchingUserAPI.getInCurrentTimeframeCount(
          this.mentorUserClass._class,
          timeframe._id,
          filter,
        )();
        return result;
      }),
    ),
  );

  @state() private _mentors = new RXController(this, this.mentorPagination.data);

  @state() private _showMenteeSorting = false;
  private _menteeFilter = new AdornisFilter({ searchFields });

  protected menteePagination = new SubscriptionPagination(
    (skip: number, limit: number, filter: AdornisFilter) => () =>
      this._currentlySelectedTimeframe.observable.pipe(
        switchMap(timeframe =>
          timeframe
            ? subscribeUser(BuddyUserMentee, this.menteeUserClass._class, timeframe._id, filter, skip, limit)
            : of([]),
        ),
      ),
    [this._menteeFilter],
    selectionSet(() => this.menteeUserClass, 3),
  );

  protected totalMentees = new RXController(
    this,
    combineLatest([this._currentlySelectedTimeframe.observable, this._menteeFilter.whenChanging]).pipe(
      switchMap(async ([timeframe, filter]) => {
        if (!timeframe) return 0;
        const result = await MatchingUserAPI.getInCurrentTimeframeCount(
          this.menteeUserClass._class,
          timeframe._id,
          filter,
        )();
        return result;
      }),
    ),
  );

  @state() private _mentees = new RXController(this, this.menteePagination.data);
  @state() protected _matches = new RXController<Match[]>(this, []);

  private readonly _openPersons = new SetBehaviorSubject(new Set<string>());
  // @ts-expect-error just used to trigge rerenders on openPersons update
  @state() private readonly _openPersonsRender = new RXController(this, this._openPersons);

  @state() windowSize = new WindowSizeController(this);

  // User-resizable side column widths (mentee left, mentor right). Persisted in
  // localStorage. Drag the dividers between sections to adjust. Default falls
  // back to the previous viewport-aware width so users who never resize see
  // the same layout as before.
  private static readonly _COLUMN_WIDTHS_KEY = 'matching-page-column-widths';
  private static readonly _COLUMN_WIDTH_MIN = 250;
  private static readonly _COLUMN_WIDTH_MAX = 800;

  @state() private _columnWidths: { left: number; right: number } = (() => {
    try {
      const raw = localStorage.getItem(BuddyPageMatching._COLUMN_WIDTHS_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (typeof parsed?.left === 'number' && typeof parsed?.right === 'number') return parsed;
      }
    } catch {
      // ignore
    }
    const fallback = typeof window !== 'undefined' && window.innerWidth < 1200 ? 300 : 400;
    return { left: fallback, right: fallback };
  })();

  private _persistColumnWidths() {
    try {
      localStorage.setItem(BuddyPageMatching._COLUMN_WIDTHS_KEY, JSON.stringify(this._columnWidths));
    } catch {
      // ignore
    }
  }

  private _startResize(side: 'left' | 'right', e: PointerEvent) {
    e.preventDefault();
    const startX = e.clientX;
    const startWidth = this._columnWidths[side];
    const target = e.currentTarget as HTMLElement;
    target.setPointerCapture(e.pointerId);
    const onMove = (ev: PointerEvent) => {
      const delta = ev.clientX - startX;
      // dragging right grows the left column and shrinks the right column
      const next = side === 'left' ? startWidth + delta : startWidth - delta;
      const clamped = Math.max(
        BuddyPageMatching._COLUMN_WIDTH_MIN,
        Math.min(BuddyPageMatching._COLUMN_WIDTH_MAX, next),
      );
      this._columnWidths = { ...this._columnWidths, [side]: clamped };
    };
    const onUp = (ev: PointerEvent) => {
      target.releasePointerCapture(ev.pointerId);
      target.removeEventListener('pointermove', onMove);
      target.removeEventListener('pointerup', onUp);
      this._persistColumnWidths();
    };
    target.addEventListener('pointermove', onMove);
    target.addEventListener('pointerup', onUp);
  }

  private _resizeHandle(side: 'left' | 'right') {
    return html`
      <div
        ${css({
          height: '100%',
          width: '6px',
          cursor: 'col-resize',
          background: this.colors.neutral.shade(100),
          userSelect: 'none',
          touchAction: 'none',
        })}
        @pointerdown=${(e: PointerEvent) => this._startResize(side, e)}
      ></div>
    `;
  }

  @state() private readonly mailTemplates = new RXController(
    this,
    getBuddyMailTemplates()(selectionSet(() => BuddyMailTemplate, 2)),
  );

  override connectedCallback(): void {
    super.connectedCallback();
    this._mentorFilter.whenChanging.subscribe(filter => {
      this.mentorPagination.updateParameters(filter);
    });
    this._menteeFilter.whenChanging.subscribe(filter => {
      this.menteePagination.updateParameters(filter);
    });
  }

  override disconnectedCallback(): void {
    super.disconnectedCallback();
    this.mentorPagination.stop();
    this.menteePagination.stop();
  }

  override render() {
    if (this._mentors.isLoading || this._mentees.isLoading) return html` <x-loading-state></x-loading-state> `;

    const mentors = (this._mentors.value ?? []) as any;
    const mentees = (this._mentees.value ?? []) as any;
    // minmax(0, Npx) lets the side columns shrink below the user's preferred width when the
    // viewport is too narrow, so we don't force horizontal scroll on smaller screens.
    const leftCol = this._menteeOpen ? `minmax(0, ${this._columnWidths.left}px)` : 'max-content';
    const rightCol = this._mentorsOpen ? `minmax(0, ${this._columnWidths.right}px)` : 'max-content';
    return html`
      <x-flex
        space="lg"
        h100
        padding="md"
        ${css({ position: 'relative', paddingBottom: '0', overflowX: 'hidden', minWidth: '0' })}
      >
        <x-flex horizontal space="md" crossaxis-center>
          <x-h1> Matching </x-h1>
        </x-flex>

        ${this._buttonBar(this._mentors.value ?? [], this._mentees.value ?? [])}
        <x-grid
          flex
          space="xs"
          columns="${leftCol} 6px 3fr 6px ${rightCol}"
          rows="1em 1fr"
          ${css({ minHeight: '0', maxHeight: 'calc(100vh - 200px)' })}
        >
          <x-flex horizontal crossaxis-center space="xs" ${css({ cursor: 'pointer' })}>
            ${
              this._menteeOpen
                ? html`
                    <x-icon
                      ${css({ fontSize: '20px' })}
                      @click=${() => {
                        this._menteeOpen = !this._menteeOpen;
                      }}
                    >
                      chevron_left
                    </x-icon>
                  `
                : nothing
            }
            <x-h3>
              ${
                this._menteeOpen
                  ? html`
                      ${this._translator.translate('user', 'plural' as any, getMenteeUserClass()._class)}
                      (${this.totalMentees.value ?? 0})
                    `
                  : html` &nbsp; `
              }
            </x-h3>
          </x-flex>
          <div></div>
          <x-h3 ${css({ textAlign: 'center' })}> Matches </x-h3>
          <div></div>
          <x-flex horizontal crossaxis-center space="xs" ${css({ cursor: 'pointer' })}>
            ${
              this._mentorsOpen
                ? html`
                    <x-icon
                      ${css({ fontSize: '20px' })}
                      @click=${() => {
                        this._mentorsOpen = !this._mentorsOpen;
                      }}
                    >
                      chevron_right
                    </x-icon>
                  `
                : nothing
            }
            <x-h3>
              ${
                this._mentorsOpen
                  ? html`
                      ${this._translator.translate('user', 'plural' as any, getMentorUserClass()._class)}
                      (${this.totalMentors.value ?? 0})
                    `
                  : html` &nbsp; `
              }
            </x-h3>
          </x-flex>
          ${
            this._menteeOpen
              ? this._persons(mentees, this.menteeUserClass, this._menteeFilter, index => {
                  this.virtualizationOnRender(index, this.menteePagination);
                })
              : this._emptyDiv('keyboard_arrow_left', () => (this._menteeOpen = true))
          }
          ${this._menteeOpen ? this._resizeHandle('left') : html`<tr-vr></tr-vr>`} ${this._matchesColumn()}
          ${this._mentorsOpen ? this._resizeHandle('right') : html`<tr-vr></tr-vr>`}
          ${
            this._mentorsOpen
              ? this._persons(mentors, this.mentorUserClass, this._mentorFilter, index => {
                  this.virtualizationOnRender(index, this.mentorPagination);
                })
              : this._emptyDiv('keyboard_arrow_right', () => (this._mentorsOpen = true))
          }
        </x-grid>
      </x-flex>
    `;
  }

  protected _buttonBar(mentors: BuddyUserMentor[], mentees: BuddyUserMentee[]) {
    const matches = this._matches.value;
    const notAllMatchesFilled = !matches.length || matches.some(m => !m.mentor || !m.mentee);

    return html`
      <x-flex horizontal space="md" end>
        <x-button
          mode="outline"
          icon="autorenew"
          @click=${async () => {
            // const allFieldsForAutoMatching = [
            //   'genderPreference',
            //   'gender',
            //   'foreignLanguages',
            //   'motherTongue',
            //   'faculty',
            // ];

            const autoMatches = await XNativeDialog.waitFor(this._matchUsers(mentors as any, mentees as any));
            this._matches.next(autoMatches);
            void XSnackbar.show(
              this._translator.translate('buddy', 'createMatchesSuccessful').replace('#', autoMatches.length + ''),
            );
          }}
        >
          ${
            automatichingRuleDescription
              ? html`
                  <x-tooltip
                    >${html`${automatichingRuleDescription.text
                      ?.split('\n')
                      .map(it => [it, html`<br />`])
                      .flat()}`}
                  </x-tooltip>
                `
              : nothing
          }
          Auto-Matching
        </x-button>
        <x-button
          ?disabled=${notAllMatchesFilled}
          @click=${async () => {
            try {
              if (notAllMatchesFilled) return;

              const confirm = await XDialog.confirm(this._translator.translate('buddy', 'createMatchesConfirm'));
              if (!confirm) return;

              const newMatches = matches.map(m => {
                return new Match({
                  mentorID: m.mentor?._id,
                  menteeID: m.mentee?._id,
                  timeframeID: this._currentlySelectedTimeframe.value?._id,
                });
              });
              await XDialog.waitFor(MatchAPI.create(newMatches)());

              this.requestUpdate();
              this._matches.next([]);
              void XSnackbar.show(this._translator.translate('buddy', 'createMatchesSuccessful'));
              void goTo('/matches');
            } catch (e) {
              logger.error(e, 'Something went wrong creating the matches.');
            }
          }}
        >
          ${this._translator.translate('buddy', 'createMatches')}
        </x-button>
      </x-flex>
    `;
  }

  private _emptyDiv(icon: string, clickAction: () => void) {
    return html`
      <x-flex
        center
        crossaxis-center
        @click=${() => clickAction()}
        ${acss({
          cursor: 'pointer',
          'x-icon': { transition: 'color .2s, transform .2s' },
          '&:hover x-icon': { color: this.colors.accent, transform: 'scale(1.5)' },
        })}
      >
        <x-icon ${css({ fontSize: this.spacing.lg })}> ${icon} </x-icon>
      </x-flex>
    `;
  }

  private _matchesColumn() {
    return html`
      <x-flex space="sm" ${css({ minHeight: '0' })}>
        ${this._createMatchBox()}
        <x-flex flex ${css({ minHeight: '0', overflowY: 'auto', paddingBottom: this.spacing.md })}>
          ${repeat(this._matches.value ?? [], match => this._matchDisplay(match))}
        </x-flex>
      </x-flex>
    `;
  }

  private _matchDisplay(match: Match) {
    return html`
      <x-card padding="sm" ${css({ width: '100%' })}>
        <x-flex horizontal end>
          <x-button
            icon="delete"
            mode="text"
            tone="error"
            @click=${async () => {
              if (!(await XNativeDialog.confirm('Vorläufiges Match auflösen?'))) return;
              this._matches.next(this._matches.value.filter(innerMatch => innerMatch !== match));
            }}
          >
            Match entfernen
          </x-button>
        </x-flex>
        <x-flex horizontal space="xs" w100>
          ${match.mentee ? this._personToggle(match.mentee, true) : this._fillMatchBox(match, 'mentee')}
          ${match.mentor ? this._personToggle(match.mentor, true) : this._fillMatchBox(match, 'mentor')}
        </x-flex>
      </x-card>
    `;
  }

  private _persons(
    users: BuddyUser[],
    userClass: typeof BuddyUserMentee | typeof BuddyUserMentor,
    filter: AdornisFilter,
    onReachEnd: (index: number) => void,
  ) {
    return html`
      <x-flex space="md" ${css({ paddingBottom: this.spacing.md, maxHeight: '100%', minHeight: '0' })}>
        <x-flex space="xs">
          <x-hr></x-hr>
          ${BHFilterEditor.render({
            fullWidth: true,
            options: buildFullOptions(
              {
                class: userClass,
                view: new View({ filter: filter }),
              },
              {
                searchable: true,
                fieldAspectGeneration: {
                  shouldIncludeField: (bqlFieldDef, path, baseEntity) =>
                    ![
                      '_class',
                      '_id',
                      'username',
                      'roles',
                      'isOnline',
                      'ios_sub',
                      'emailConfirmed',
                      'timeframeID',
                      'matches',
                      'hiddenFromMentees',
                    ].includes(bqlFieldDef.name),
                },
                overridesForIdentifier: {
                  numMatches: IntVariations,
                },
              },
            ).filterOptions,
          })}
          <x-hr></x-hr>
          ${
            (userClass === this.menteeUserClass ? this._showMenteeSorting : this._showMentorSorting)
              ? html`
                  <x-flex space="xs" ${css({ userSelect: 'none', cursor: 'pointer' })}>
                    <x-flex
                      horizontal
                      space="xs"
                      crossaxis-center
                      @click=${() => {
                        if (userClass === this.menteeUserClass) this._showMenteeSorting = false;
                        else this._showMentorSorting = false;
                      }}
                    >
                      <x-icon>keyboard_arrow_up</x-icon>
                      <x-text>${this._translator.translate('field', 'sortingHide')}</x-text>
                    </x-flex>
                    <x-flex horizontal space="xs" padding="xs" flex>
                      <x-dropdown-selection
                        flex
                        placeholder="${this._translator.translate('field', 'sortingChooseField' as any)}"
                        .value=${filter.sortingFields[0]}
                        @value-picked=${(choiseEvt: ValueEvent<string>) => {
                          const choise = choiseEvt.detail.value;
                          if (!choise) return;

                          filter.sortingFields = [choiseEvt.detail.value];
                          filter.sortingDirections = ['asc'];
                        }}
                        .selectables=${Object.keys(
                          (userClass === this.menteeUserClass ? this.menteeUserClass : this.mentorUserClass).fields,
                        ).filter(curAttr => !HiddenSortingMachingUserFields.includes(curAttr))}
                        .renderString=${(fieldAttr: string) => this._translator.translate('field', fieldAttr as any)}
                      ></x-dropdown-selection>
                      <x-dropdown-selection
                        flex
                        placeholder="${this._translator.translate('field', 'sortingChooseDirection' as any)}"
                        .value=${filter.sortingDirections[0] ?? 'asc'}
                        @value-picked=${(choiseEvt: ValueEvent<boolean>) => {
                          const choise = choiseEvt.detail.value;

                          filter.sortingDirections = [choise ? 'asc' : 'desc'];
                        }}
                        .selectables=${[true, false]}
                        .renderString=${(isDesc: boolean) =>
                          this._translator.translate('field', (isDesc ? 'sortingAsc' : 'sortingDesc') as any)}
                      ></x-dropdown-selection>
                    </x-flex>
                  </x-flex>
                `
              : html`
                  <x-flex
                    horizontal
                    space="xs"
                    crossaxis-center
                    @click=${() => {
                      if (userClass === this.menteeUserClass) this._showMenteeSorting = true;
                      else this._showMentorSorting = true;
                    }}
                    ${css({ userSelect: 'none', cursor: 'pointer' })}
                  >
                    <x-icon>keyboard_arrow_down</x-icon>
                    <x-text>${this._translator.translate('field', 'sorting')}</x-text>
                  </x-flex>
                `
          }
          <x-hr></x-hr>
        </x-flex>

        <x-button
          mode="outline"
          @click=${async () => {
            const filter = (() => {
              if (userClass === this.menteeUserClass) return this._menteeFilter;
              else return this._mentorFilter;
            })();
            const _clazz = (() => {
              if (userClass === this.menteeUserClass) return this.menteeUserClass._class;
              else return this.mentorUserClass._class;
            })();
            const timeframeID = this._currentlySelectedTimeframe.value?._id;
            if (!timeframeID) throw new Error('timeframe not selected');
            const users = await XDialog.waitFor(
              firstValueFrom(subscribeUser(userClass, _clazz, timeframeID, filter, 0, 999999)),
            );
            return this.sendMailToUsers(users as any);
          }}
        >
          Rundmail senden
        </x-button>

        <x-flex ${css({ overflowY: 'auto', flexGrow: '1', minHeight: '0' })}>
          <x-drag-drop
            flex
            child-selector="[buddy]"
            allow-from="[buddy]"
            duplicate
            .duplicateFn=${(staticEl, movingEl) => {
              movingEl.user = staticEl.user;
              const staticElBounding = staticEl.getBoundingClientRect();
              const movingElUserDetail = movingEl.querySelector(this._personTogleComponentName);
              if (!movingElUserDetail) return;
              movingElUserDetail.user = staticEl.user;
              movingEl.style.width = staticElBounding.width + 'px';
            }}
          >
            <x-flex h100 w100 space="sm" ${css({ minWidth: '0', padding: '2px' })}>
              ${repeat(users, (user, index) => this._personCard(user, index, onReachEnd))}
            </x-flex>
          </x-drag-drop>
        </x-flex>
      </x-flex>
    `;
  }

  private _personCard(user: BuddyUser, index: number, onLoad: (index: number) => void) {
    return virtualize(
      html`
        <x-card buddy can-handle padding="sm" .user=${user} ${css({ width: '100%', userSelect: 'none' })}>
          ${this._personToggle(user)}
        </x-card>
      `,
      oldHeight => html` <div ${css({ height: `${oldHeight}px`, gridColumn: '1 / -1' })}></div> `,
      {
        wrapperDisplayContents: true,
        onRender: () => {
          onLoad(index);
        },
      },
    );
  }

  protected virtualizationOnRender(index: number, pagination: SubscriptionPagination<any, any>) {
    if (
      (index === pagination.data.length - 1 || index === pagination.data.length - 1) &&
      pagination.totalRequested.value < index + 80
    ) {
      pagination.next();
    }
  }

  private _createMatchBox() {
    return html`
      <x-drag-drop
        matching
        child-selector="[buddy]"
        dropzone-accept-selector="[buddy]"
        @dropped=${(e: CustomEvent<{ user: BuddyUser }>) => {
          const user = e.detail.user;
          const newMatch = new Match({});
          if (isMentor(user)) {
            newMatch.mentor = user;
            newMatch.mentorID = user._id;
          }
          if (isMentee(user)) {
            newMatch.mentee = user;
            newMatch.menteeID = user._id;
          }
          this._matches.next([newMatch, ...this._matches.value]);
        }}
      >
        <x-flex center crossaxis-center padding="xs" w100>
          <x-text ${css({ color: this.colors.accent })}>
            ${this._translator.translate('buddy', 'dragPersonHere')}
          </x-text>
        </x-flex>
      </x-drag-drop>
    `;
  }

  private _fillMatchBox(match: Match, allow: 'mentor' | 'mentee') {
    return html`
      <x-drag-drop
        matching
        flex
        child-selector="[buddy]"
        dropzone-accept-selector="[buddy]"
        @dropped=${(e: CustomEvent) => {
          const user = e.detail.user as Maybe<BuddyUser>;
          if (!user) return;
          if (
            (user instanceof BuddyUserMentor && allow === 'mentee') ||
            (user instanceof BuddyUserMentee && allow === 'mentor')
          ) {
            return;
          }

          if (isMentor(user)) {
            match.mentorID = user._id;
            match.mentor = user;
          }
          if (isMentee(user)) {
            match.menteeID = user._id;
            match.mentee = user;
          }
          this.requestUpdate();
        }}
        ${css({ height: '100%', minHeight: '80px' })}
      >
        <x-flex center crossaxis-center padding="xs" w100>
          <x-text ${css({ color: this.colors.accent })}>
            ${this._translator.translate('buddy', 'dragPersonHere')}
          </x-text>
        </x-flex>
      </x-drag-drop>
    `;
  }

  protected _personToggle(user: BuddyUser, alwaysOpen?: boolean) {
    return html`
      <buddy-user-details ?always-open=${alwaysOpen} flex ${css({ minWidth: '0' })} .user=${user}></buddy-user-details>
    `;
  }

  protected async _matchUsers(mentors: DefaultMentorUser[], mentees: DefaultMenteeUser[]) {
    return await autoMatchUsers(
      this._currentlySelectedTimeframe.value!._id,
      AutoMatchUserEntry.fromParams(this.menteeUserClass._class, this._menteeFilter),
      AutoMatchUserEntry.fromParams(this.mentorUserClass._class, this._mentorFilter),
    )(selectionSet(() => Match, 3));
  }

  override styles = () => {
    return [
      {
        ':host': { height: '100%' },
        '[matching]': {
          width: '100%',
          height: '80px',
          border: `2px dashed ${this.colors.accent.shade(200)}`,
          borderRadius: this.sizes.borderRadius,
          boxSizing: 'border-box',
        },
        '[matching][dropenabled]': {
          background: this.colors.accent.shade(50),
          border: `2px solid ${this.colors.accent}`,
        },
      },
    ];
  };

  async sendMailToUsers(users: NarrowedEntity<User, { _id: 1; email: 1; firstName: 1; lastName: 1 }>[]) {
    if (users.length === 0) return;
    const userType = users[0] instanceof BuddyUserMentee ? 'Mentee' : 'Mentor';

    customMailRequestDialog({
      // availableMailTemplates: this.mailTemplates.value ?? [],
      // availableMailTemplates: Object.values(mailTemplateDefinitions),
      availableMailTemplates: [
        new BuddyMailTemplate({
          emailType: 'Free_Form',
          scope: 'default',
          name: 'Freie Mail',
          content: mailTemplateDefinitions[`Free_Form_${userType}`]?.defaultTemplate.content,
          subject: mailTemplateDefinitions[`Free_Form_${userType}`]?.defaultTemplate.subject,
        }),
        ...(this.mailTemplates.value as any)?.filter(mailTemplate => mailTemplate.emailType.includes(userType)),
      ],

      mails: await Promise.all(users.map(user => createUserMailEditorSettings(user))),
      sendMails: mails => sendCustomMatchingMail(mails)(),
    });
  }
}

function isMentor(user: BuddyUser): user is BuddyUserMentor {
  return user instanceof BuddyUserMentor;
}

function isMentee(user: BuddyUser): user is BuddyUserMentee {
  return user instanceof BuddyUserMentee;
}

async function createUserMailEditorSettings(user: NarrowedEntity<User, { _id: 1; email: 1 }>) {
  const userVarsString = await getUserMailVars(user._id)();
  const userVars = JSON.parse(userVarsString ?? '{}') ?? {};
  return {
    mailAddresses: [user.email],
    contextVariables: userVars,
  };
}

function subscribeUser<T extends typeof BuddyUser>(clazz: T, _clazz, timeframeID: string, filter, skip, limit) {
  console.trace({ clazz });
  return MatchingUserAPI.subscribeAllInCurrentTimeframe(
    _clazz,
    timeframeID,
    filter,
    skip,
    limit,
  )(selectionSet(() => clazz as any, 3)) as Observable<T['prototype'][]>;
}
